/**
 * How long a DM send request may hang before it is treated as failed.
 *
 * MEASURED DEFECT (port-0921-chatnotif-01-send-timeout, web-stg.iyuga.net,
 * bundle main.27cbff47.js, 2026-09-22 — Playwright route interception holding
 * `POST /api/v1/dm/conversations/{id}/messages/` open and never answering
 * it): the Composer's Send control stayed disabled and the typed message
 * stayed stuck, unsent, in the input for the full 25s+ observation window
 * with ZERO error, ZERO "Not delivered · Retry" state, and no way to send
 * anything else in the thread short of a full page reload. A positive
 * control on the same rig (forcing a real 500 instead of a hang) DID surface
 * "Not delivered · Retry" within 5s, proving the probe correctly
 * distinguishes a hang from a real error — this is a hang-specific gap.
 *
 * This is the exact same defect class `loginTimeout.js` already fixed for
 * the sign-in button (see that file's header comment, `LOGIN_TIMEOUT_MS`) —
 * a request that never settles is worse than one that errors, because
 * `useSendMessage`'s existing `onError` (already wired to flip the optimistic
 * bubble to the "failed"/"Not delivered · Retry" state, `src/services/chat.js`)
 * never runs if the promise never rejects. Axios turns an exceeded `timeout`
 * into a rejection, which reaches that same `onError` — no new error-handling
 * branch, no new copy, just a bounded wait.
 *
 * Ported verbatim from dev PR #5919 (fix-stg0921-chat-send-hang-dev, merged
 * to dev 2026-09-21) — same constants, same reasoning: 20s matches
 * `LOGIN_TIMEOUT_MS`'s own reasoning: generous enough that a real,
 * slow-but-alive send is never mistaken for a hang, short enough that a user
 * is told something well inside the "did this just eat my message" window.
 */
export const CHAT_SEND_TIMEOUT_MS = 20000;

/**
 * Same defect, the ATTACHMENT (multipart) send path — deliberately a separate,
 * longer bound rather than reusing `CHAT_SEND_TIMEOUT_MS`: an upload can
 * legitimately take longer than a text-only send (up to
 * `CHAT_MEDIA_MAX_UPLOAD_BYTES` = 25MB, messaging/api/v1/views.py, over a real
 * user's upload bandwidth), so 20s would abort some genuinely-in-progress
 * uploads, trading the hang bug for a new false-timeout bug. 60s still bounds
 * a true hang; a real 25MB upload completes well inside it on any connection
 * that isn't already failing.
 */
export const CHAT_SEND_ATTACHMENT_TIMEOUT_MS = 60000;
